/**
 * AP-29-N-I — Die Preisliste des Betreibers.
 *
 * Preise standen bis hierher nur hinter der API. Der Katalog war damit halb
 * bedienbar: Inhalte ließen sich zusammenstellen, Beträge nicht.
 *
 * **Gepflegt wird, was sich verkaufen lässt** — jeder buchbare Baustein, jedes
 * eigenständige Modul, jedes Paket. Eine Gruppe (`TIME_TRACKING`) steht hier
 * nicht: Sie ist kein Angebot, sondern eine Überschrift.
 *
 * Zwei Sätze tragen die Seite, und sie stehen auch darauf:
 *
 * 1. **Ein Preis ist keine Zahl.** Ohne Bezug (je Platz / pauschal) und Periode
 *    ist ein Betrag nicht interpretierbar — die Website verkauft „pro
 *    Mitarbeiter / Monat", ein API-Zugang dagegen ist pauschal.
 * 2. **Eine Änderung wirkt nie rückwirkend.** Was ein Bestandskunde zahlt, steht
 *    an seiner Buchung und bleibt unberührt. Genau deshalb darf hier jederzeit
 *    neu bepreist werden.
 *
 * Bewusst keine Listen-Standard-Seite: eine kurze, feste Liste, die man pflegt —
 * keine Auswertung, die man filtert und exportiert.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Euro, Check, Trash2 } from 'lucide-react'
import { MODULE_REGISTRY, componentsOf, isBookable, isModuleGroup } from '@symphenia/shared-types'
import { formCard, pageWide } from '@/lib/form-styles'
import { useTranslation } from 'react-i18next'
import { moduleLabel } from '@/lib/module-label'
import { operatorApi, type ModulePackage, type CatalogRow } from '../api/operator.api'

type Bezug = CatalogRow['basis']
type Periode = CatalogRow['period']

/** Was in einer Zeile bearbeitet wird — leerer Betrag heißt „kein Preis". */
type Entwurf = {
  salesName: string
  amount: string
  basis: Bezug
  period: Periode
}

const LEER: Entwurf = {
  salesName: '',
  amount: '',
  basis: 'PER_SEAT',
  period: 'MONTH',
}

function meldung(e: unknown, ersatz: string): string {
  const antwort = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message
  return Array.isArray(antwort) ? antwort.join(' · ') : (antwort ?? ersatz)
}

/**
 * Ein Eintrag der Liste — Gruppe, Baustein, eigenständiges Modul oder Paket.
 *
 * Die **Gruppe** trägt seit AP-29-N-J eine eigene Zeile, aber kein Betragsfeld:
 * Beschriftet werden darf alles, was es gibt; bepreist nur, was man kaufen kann.
 */
type Posten = {
  key: string
  name: string
  einzug: boolean
  art: 'gruppe' | 'modul' | 'paket'
}

export default function OperatorPricesPage() {
  const [preise, setPreise] = useState<Map<string, CatalogRow>>(new Map())
  const [pakete, setPakete] = useState<ModulePackage[]>([])
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gespeichert, setGespeichert] = useState<string | null>(null)

  const laden = useCallback(async () => {
    setLoading(true)
    setFehler(null)
    try {
      const [liste, katalog] = await Promise.all([
        operatorApi.listCatalog(),
        operatorApi.listPackages(true),
      ])
      setPreise(new Map(liste.map((p) => [p.itemKey, p])))
      setPakete(katalog)
    } catch (e) {
      setFehler(meldung(e, 'Die Preisliste konnte nicht geladen werden.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  /**
   * Die Posten in Anzeigereihenfolge: Bausteine unter ihrer Gruppe eingerückt,
   * eigenständige Module und Pakete darunter.
   *
   * Die **Gruppe steht selbst in der Liste**, aber ohne Betragsfeld: Sie ist
   * kein Angebot — gekauft werden ihre Bausteine —, sie ist aber die
   * Überschrift, unter der der Kunde sie sieht, und darum will der Betrieb sie
   * benennen können. Bis zum 12.9.2026 war sie nur eine tote Zeile.
   */
  const posten = useMemo<{ gruppe: string | null; zeilen: Posten[] }[]>(() => {
    const abschnitte: { gruppe: string | null; zeilen: Posten[] }[] = []

    for (const m of MODULE_REGISTRY) {
      if (!isModuleGroup(m.key)) continue
      const teile = componentsOf(m.key).filter((t) => isBookable(t.key))
      if (teile.length) {
        abschnitte.push({
          gruppe: m.key,
          zeilen: [
            { key: m.key, name: m.key, einzug: false, art: 'gruppe' as const },
            ...teile.map((t) => ({
              key: t.key,
              name: t.key,
              einzug: true,
              art: 'modul' as const,
            })),
          ],
        })
      }
    }

    const einzeln = MODULE_REGISTRY.filter((m) => isBookable(m.key) && !m.parent)
    if (einzeln.length) {
      abschnitte.push({
        gruppe: null,
        zeilen: einzeln.map((m) => ({
          key: m.key,
          name: m.key,
          einzug: false,
          art: 'modul',
        })),
      })
    }

    if (pakete.length) {
      abschnitte.push({
        gruppe: 'Pakete',
        zeilen: pakete.map((p) => ({
          key: p.key,
          name: p.name,
          einzug: true,
          art: 'paket',
        })),
      })
    }

    return abschnitte
  }, [pakete])

  const speichern = async (key: string, entwurf: Entwurf) => {
    setFehler(null)
    try {
      // Nur wenn BEIDE Angaben leer sind, verschwindet die Zeile. Ein leerer
      // Betrag allein heisst "noch nicht bepreist" -- die Bezeichnung bleibt.
      if (entwurf.amount.trim() === '' && entwurf.salesName.trim() === '') {
        await operatorApi.removeCatalogEntry(key)
        setPreise((prev) => {
          const next = new Map(prev)
          next.delete(key)
          return next
        })
      } else {
        const { data } = await operatorApi.setCatalogEntry(key, {
          salesName: entwurf.salesName.trim() || undefined,
          amount:
            entwurf.amount.trim() === '' ? undefined : Number(entwurf.amount.replace(',', '.')),
          currency: 'EUR',
          basis: entwurf.basis,
          period: entwurf.period,
        })
        setPreise((prev) => new Map(prev).set(key, data))
      }
      setGespeichert(key)
      window.setTimeout(() => setGespeichert((g) => (g === key ? null : g)), 1500)
    } catch (e) {
      setFehler(meldung(e, `Der Preis für '${key}' konnte nicht gespeichert werden.`))
    }
  }

  return (
    <div className={pageWide}>
      <div>
        <Link
          to="/admin/pakete"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Pakete
        </Link>
        <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
          <Euro size={26} className="text-primary" /> Preise
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Je Baustein und je Paket.{' '}
          <strong className="text-foreground">Ein Preis ist keine Zahl</strong> — ohne Bezug und
          Zeitraum lässt sich ein Betrag nicht verrechnen. Eine Änderung hier wirkt{' '}
          <strong className="text-foreground">nie rückwirkend</strong>: Was ein Bestandskunde zahlt,
          steht an seiner Buchung. Ein leerer Betrag heißt „noch nicht bepreist" — das ist ein
          zulässiger Zustand, kein Fehler.
        </p>
      </div>

      {fehler && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fehler}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      ) : (
        <div className={formCard}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="pb-3 pr-4">Posten</th>
                <th className="pb-3 pr-4">Vertriebsbezeichnung</th>
                <th className="pb-3 pr-4 w-32">Betrag</th>
                <th className="pb-3 pr-4 w-36">Bezug</th>
                <th className="pb-3 pr-4 w-32">Zeitraum</th>
                <th className="pb-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {posten.map((abschnitt) => (
                <PreisAbschnitt
                  key={abschnitt.gruppe ?? 'einzeln'}
                  abschnitt={abschnitt}
                  preise={preise}
                  gespeichert={gespeichert}
                  onSpeichern={speichern}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function PreisAbschnitt({
  abschnitt,
  preise,
  gespeichert,
  onSpeichern,
}: {
  abschnitt: { gruppe: string | null; zeilen: Posten[] }
  preise: Map<string, CatalogRow>
  gespeichert: string | null
  onSpeichern: (key: string, entwurf: Entwurf) => Promise<void>
}) {
  return (
    <>
      {abschnitt.gruppe === null && (
        <tr>
          <td
            colSpan={6}
            className="pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Einzelne Module und Pakete
          </td>
        </tr>
      )}
      {abschnitt.zeilen.map((p) => (
        <PreisZeile
          key={p.key}
          posten={p}
          preis={preise.get(p.key) ?? null}
          gerade={gespeichert === p.key}
          onSpeichern={(entwurf) => onSpeichern(p.key, entwurf)}
        />
      ))}
    </>
  )
}

function PreisZeile({
  posten,
  preis,
  gerade,
  onSpeichern,
}: {
  posten: Posten
  preis: CatalogRow | null
  gerade: boolean
  onSpeichern: (entwurf: Entwurf) => Promise<void>
}) {
  const { t } = useTranslation()
  /** Was ohne eigene Bezeichnung angezeigt würde — steht als Platzhalter da. */
  const vorgabe = moduleLabel(posten.key, t)

  const start: Entwurf = preis
    ? {
        salesName: preis.salesName ?? '',
        amount: preis.amount === null ? '' : String(preis.amount).replace('.', ','),
        basis: preis.basis,
        period: preis.period,
      }
    : LEER

  const [e, setE] = useState<Entwurf>(start)
  const [speichert, setSpeichert] = useState(false)

  // Nach dem Neuladen der Liste den Entwurf nachziehen — sonst zeigt die Zeile
  // noch, was vor dem Speichern darin stand.
  useEffect(() => {
    setE(start)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preis?.salesName, preis?.amount, preis?.basis, preis?.period])

  const geaendert =
    e.salesName !== start.salesName ||
    e.amount !== start.amount ||
    e.basis !== start.basis ||
    e.period !== start.period

  const speichern = async () => {
    setSpeichert(true)
    try {
      await onSpeichern(e)
    } finally {
      setSpeichert(false)
    }
  }

  return (
    <tr className="align-middle">
      <td className={`py-2 pr-4 ${posten.einzug ? 'pl-4' : ''} text-foreground`}>
        <span className={posten.art === 'paket' ? 'font-medium' : 'font-mono text-xs'}>
          {posten.name}
        </span>
        {posten.art === 'gruppe' && (
          <span className="ml-2 text-xs text-muted-foreground">Gruppe — nicht buchbar</span>
        )}
      </td>

      <td className="py-2 pr-4">
        <input
          value={e.salesName}
          onChange={(ev) => setE({ ...e, salesName: ev.target.value })}
          placeholder={vorgabe}
          aria-label={`Bezeichnung für ${posten.key}`}
          className="w-full min-w-48 rounded-md border border-border bg-background px-2 py-1 text-sm"
        />
      </td>

      {posten.art === 'gruppe' ? (
        // Kein Betragsfeld: Ein Preis auf einer Gruppe waere ein Angebot ins
        // Leere. Drei leere Zellen, damit die Spalten der Tabelle stehen bleiben.
        <>
          <td className="py-2 pr-4" />
          <td className="py-2 pr-4" />
          <td className="py-2 pr-4" />
        </>
      ) : (
        <>
          <td className="py-2 pr-4">
            <input
              value={e.amount}
              onChange={(ev) => setE({ ...e, amount: ev.target.value })}
              placeholder="—"
              inputMode="decimal"
              aria-label={`Betrag für ${posten.key}`}
              className="w-24 rounded-md border border-border bg-background px-2 py-1 text-sm"
            />
            <span className="ml-1 text-xs text-muted-foreground">€</span>
          </td>

          <td className="py-2 pr-4">
            <select
              value={e.basis}
              onChange={(ev) => setE({ ...e, basis: ev.target.value as Bezug })}
              aria-label={`Bezug für ${posten.key}`}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            >
              <option value="PER_SEAT">je Platz</option>
              <option value="FLAT">pauschal</option>
            </select>
          </td>

          <td className="py-2 pr-4">
            <select
              value={e.period}
              onChange={(ev) => setE({ ...e, period: ev.target.value as Periode })}
              aria-label={`Zeitraum für ${posten.key}`}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            >
              <option value="MONTH">pro Monat</option>
              <option value="YEAR">pro Jahr</option>
            </select>
          </td>
        </>
      )}

      <td className="py-2">
        {gerade ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
            <Check size={13} /> gespeichert
          </span>
        ) : (
          geaendert && (
            <button
              onClick={speichern}
              disabled={speichert}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
            >
              {e.amount.trim() === '' && e.salesName.trim() === '' ? <Trash2 size={12} /> : null}
              {e.amount.trim() === '' && e.salesName.trim() === ''
                ? 'Eintrag entfernen'
                : 'Speichern'}
            </button>
          )
        )}
      </td>
    </tr>
  )
}
